import { useQuery } from '@apollo/client';
import clsx from 'clsx';
import { useState } from 'react';

import {
  type ExpertRequestStage,
  type GetExpertRequestActivityQuery,
  type GetExpertRequestActivityQueryVariables,
} from '@/__generated__/graphql';
import Button from '@/componentsv2/Button';
import { GET_EXPERT_REQUEST_ACTIVITY } from '@/expertrequest/store/queries';

import { ER_STAGE_LABEL } from '../erStageTokens';
import ViewChangesModal from './ViewChangesModal';
import { activityFullTime, activityRowTime, groupByDay } from './activityFeedHelpers';

export const ACTIVITY_PAGE_SIZE = 25;

type VisibilityFilter = 'all' | 'customer';

type ActivityEntry = NonNullable<
  GetExpertRequestActivityQuery['expertRequest']
>['activity_feed'][number];

interface ActivityFeedProps {
  expertRequestId: string;
  /** Staff (admin or research team) see internal rows and the All / Customer toggle. */
  canSeeInternal: boolean;
}

// A few field keys read better than a naive humanization; everything else falls back.
const FIELD_LABELS: Record<string, string> = {
  // The ER's `name` column is always surfaced to users as "Title" (reads right to
  // both customers and staff, even though the field is technically the ER name).
  name: 'Title',
  origin: 'Origin',
  sourcing_scope: 'Sourcing scope',
  allow_internal_vetting: 'Internal vetting',
  allow_external_recruiting: 'External recruiting',
  questions: 'Vetting questions',
  qualifications: 'Vetting qualifications',
  er_type: 'Request type',
  expected_duration: 'Expected duration',
  focus_areas: 'Focus areas',
  companies_pursue: 'Companies to pursue',
  companies_avoid: 'Companies to avoid',
  group_about: 'Group context',
};

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
}

// A row has a renderable diff when it's a field change (not a stage transition,
// which renders inline) with at least one stored value. Vetting questions/
// qualifications now store their before/after content (ENG-5314), so they get a
// diff link too; only stage rows are excluded.
function hasRenderableDiff(entry: ActivityEntry): boolean {
  if (entry.field_name === 'stage') return false;
  const hasOld = entry.old_value !== null && entry.old_value !== undefined;
  const hasNew = entry.new_value !== null && entry.new_value !== undefined;
  return hasOld || hasNew;
}

function EntryLine({ entry }: { entry: ActivityEntry }): JSX.Element {
  const actor = (
    <span className="font-semibold text-on-surface">{entry.changed_by?.name ?? 'System'}</span>
  );

  if (entry.field_name === 'stage') {
    // Fall back to the raw value so an unknown/legacy stage shows verbatim, not blank.
    const newLabel = ER_STAGE_LABEL[entry.new_value as ExpertRequestStage] ?? entry.new_value;
    // A stage row with no prior value (null or undefined) is the creation event.
    if (entry.old_value === null || entry.old_value === undefined) {
      return (
        <span>
          {actor} <span className="text-on-surface-secondary">created the expert request as</span>{' '}
          <span className="font-semibold text-on-surface">{newLabel}</span>
        </span>
      );
    }
    const oldLabel = ER_STAGE_LABEL[entry.old_value as ExpertRequestStage] ?? entry.old_value;
    return (
      <span>
        {actor} <span className="text-on-surface-secondary">changed the stage from</span>{' '}
        <span className="font-semibold text-on-surface">{oldLabel}</span>{' '}
        <span className="text-on-surface-secondary">to</span>{' '}
        <span className="font-semibold text-on-surface">{newLabel}</span>
      </span>
    );
  }

  return (
    <span>
      {actor}{' '}
      <span className="text-on-surface-secondary">
        {`updated the ${fieldLabel(entry.field_name).toLowerCase()}`}
      </span>
    </span>
  );
}

export function ActivityFeed({ expertRequestId, canSeeInternal }: ActivityFeedProps): JSX.Element {
  const [filter, setFilter] = useState<VisibilityFilter>('all');
  const [activeEntry, setActiveEntry] = useState<ActivityEntry | null>(null);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);

  const { data, loading, error, fetchMore } = useQuery<
    GetExpertRequestActivityQuery,
    GetExpertRequestActivityQueryVariables
  >(GET_EXPERT_REQUEST_ACTIVITY, {
    variables: { expertRequestId, limit: ACTIVITY_PAGE_SIZE },
    notifyOnNetworkStatusChange: true,
  });

  const entries = data?.expertRequest?.activity_feed ?? [];
  // The toggle is display-only: the server already strips internal rows for non-staff.
  const visibleEntries =
    canSeeInternal && filter === 'customer' ? entries.filter((e) => !e.is_internal) : entries;

  // `loading` covers the initial fetch and fetchMore alike (notifyOnNetworkStatusChange),
  // so gating on it keeps the button from flickering away mid-fetch.
  const noMore = reachedEnd || (!loading && entries.length < ACTIVITY_PAGE_SIZE);
  const showLoadEarlier = !noMore && entries.length > 0;

  async function handleLoadEarlier() {
    // Keyset cursor needs a real timestamp; skip any trailing rows whose changed_at
    // is null (defensive — `any`-typed scalar) so `before: null` can't reset paging.
    const oldest = [...entries].reverse().find((e) => e.changed_at);
    if (!oldest) return;
    setLoadingMore(true);
    setLoadMoreError(false);
    try {
      const res = await fetchMore({
        variables: { before: oldest.changed_at, beforeId: oldest.id },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.expertRequest) return prev;
          const prevFeed = prev.expertRequest?.activity_feed ?? [];
          return {
            ...prev,
            expertRequest: {
              ...prev.expertRequest,
              ...fetchMoreResult.expertRequest,
              activity_feed: [...prevFeed, ...fetchMoreResult.expertRequest.activity_feed],
            },
          };
        },
      });
      // A missing expertRequest (error / access revoked mid-session) is not "no more
      // rows" — leave the button so the user can retry rather than silently hiding it.
      const fetched = res.data?.expertRequest?.activity_feed;
      if (fetched && fetched.length < ACTIVITY_PAGE_SIZE) {
        setReachedEnd(true);
      }
    } catch {
      // Don't fail silently — surface a retryable error and keep the button.
      // (A stale-cursor expertrequest.invalid_cursor is mirrored in apiErrorCodes
      // for contract parity; the generic retry path is sufficient here.)
      setLoadMoreError(true);
    } finally {
      setLoadingMore(false);
    }
  }

  if (error) {
    return (
      <p className="py-24 text-center text-14 text-on-surface-muted">
        Couldn’t load activity. Please try again.
      </p>
    );
  }

  if (loading && entries.length === 0) {
    return <p className="py-24 text-center text-14 text-on-surface-muted">Loading activity…</p>;
  }

  if (entries.length === 0) {
    return <p className="py-24 text-center text-14 text-on-surface-muted">No activity yet.</p>;
  }

  const dayGroups = groupByDay(visibleEntries);

  return (
    <div className="flex flex-col gap-12">
      {canSeeInternal && (
        <div className="flex items-center gap-16 self-start border-b border-surface-border">
          {(['all', 'customer'] as const).map((key) => (
            <button
              key={key}
              type="button"
              aria-pressed={filter === key}
              onClick={() => setFilter(key)}
              className={clsx(
                '-mb-px pb-6 text-12 font-medium',
                filter === key
                  ? 'border-b-2 border-brand-primary text-on-surface'
                  : 'text-on-surface-muted'
              )}
            >
              {key === 'all' ? 'All' : 'Customer'}
            </button>
          ))}
        </div>
      )}

      {visibleEntries.length === 0 ? (
        <p className="py-12 text-center text-14 text-on-surface-muted">
          {showLoadEarlier
            ? 'No customer-visible activity on these entries. Load earlier to see more.'
            : 'No customer-visible activity yet.'}
        </p>
      ) : (
        <div className="flex flex-col gap-16">
          {dayGroups.map((group) => (
            <div key={group.key} className="flex flex-col gap-6">
              <div className="text-12 font-semibold uppercase text-on-surface-muted">
                {group.label}
              </div>
              <ol className="flex flex-col">
                {group.entries.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex items-baseline justify-between gap-x-8 py-2 text-14"
                  >
                    <span>
                      <EntryLine entry={entry} />
                      {canSeeInternal && entry.is_internal && (
                        <span className="ml-6 text-12 font-medium text-on-surface-muted">
                          Internal
                        </span>
                      )}
                      {hasRenderableDiff(entry) && (
                        <button
                          type="button"
                          className="ml-8 text-12 text-brand-primary hover:text-brand-primary-hover"
                          onClick={() => setActiveEntry(entry)}
                        >
                          View changes
                        </button>
                      )}
                    </span>
                    <span
                      className="shrink-0 text-12 text-on-surface-muted"
                      title={activityFullTime(entry.changed_at)}
                    >
                      {activityRowTime(entry.changed_at)}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}

      {loadMoreError && (
        <p className="text-center text-12 text-alert-error">Couldn’t load more. Try again.</p>
      )}
      {showLoadEarlier && (
        <div className="flex justify-center">
          <Button variant="tertiary" size="small" loading={loadingMore} onClick={handleLoadEarlier}>
            Load earlier
          </Button>
        </div>
      )}

      {activeEntry && (
        <ViewChangesModal
          open
          onClose={() => setActiveEntry(null)}
          fieldLabel={fieldLabel(activeEntry.field_name)}
          oldValue={activeEntry.old_value}
          newValue={activeEntry.new_value}
        />
      )}
    </div>
  );
}
